import { ConversationLanguageCode } from 'lib-shared-languages';
import { useMemo, useState } from 'react';

import {
  TranslateTextChangeData,
  TranslateTextDropdownSelectionList,
} from 'components/translateTextDropdownSelectionList';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

export type UseTranslateTextDropdownButtonSelectionList = {
  /** The text to translate. */
  text?: string;
  /** The language code to translate the text to. */
  languageCode?: ConversationLanguageCode;
  /**
   * The original language of the text.
   *
   * @remarks
   *   The message original language can be undefined, but this prop is made
   *   mandatory. Instead, it can be null. This is done in order for the
   *   consumer not to forget to pass it, since there are so many consumers.
   */
  originalLanguageCode: ConversationLanguageCode | null;
  /** Whether the translation is disabled. */
  isDisabled?: boolean;
};

type UseTranslateTextDropdownSelectionListResult = {
  /** The dropdown button element. */
  readonly button: JSX.Element | undefined;
  /** The successful translation data. */
  readonly translation?: TranslateTextChangeData;
};

/**
 * Represents a hook for creating and handling the logic of a translate text
 * dropdown button element.
 */
export function useTranslateTextDropdownSelectionList(
  props: UseTranslateTextDropdownButtonSelectionList,
): UseTranslateTextDropdownSelectionListResult {
  const { languageCode, originalLanguageCode, text, isDisabled } = props;
  const { agentCopilot } = useFeatureFlag(['agent-copilot']);
  const [translation, setTranslation] = useState<TranslateTextChangeData>();

  const button = useMemo(() => {
    if (isDisabled || !agentCopilot || !text) {
      return undefined;
    }

    return (
      <TranslateTextDropdownSelectionList
        message={text}
        originalLanguageCode={originalLanguageCode}
        selectedLanguageCode={languageCode}
        onRevert={() => {
          setTranslation(undefined);
        }}
        onChange={(translation) => {
          setTranslation(translation);
        }}
      />
    );
  }, [agentCopilot, isDisabled, languageCode, originalLanguageCode, text]);

  return useMemo(
    () => ({
      button,
      translation,
    }),
    [button, translation],
  );
}
